import React, { useState } from 'react';
import { X, AlertTriangle, Archive, Trash2, Info } from 'lucide-react';

interface DealDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  deal: {
    id: string;
    business_name: string;
    business_alias?: string;
    description?: string;
    deal_value?: number;
    currency: string;
    created_at: string;
  };
  onDelete: (dealId: string) => Promise<void>;
  onArchive: (dealId: string, reason?: string) => Promise<void>;
  isLoading?: boolean;
}

export default function DealDeletionModal({
  isOpen,
  onClose,
  deal,
  onDelete,
  onArchive,
  isLoading = false
}: DealDeletionModalProps) {
  const [action, setAction] = useState<'archive' | 'delete' | null>(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!action) return;

    setIsSubmitting(true);
    try {
      if (action === 'archive') {
        await onArchive(deal.id, archiveReason);
      } else if (action === 'delete') {
        await onDelete(deal.id);
      }
      onClose();
    } catch (error) {
      console.error('Error processing deal:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setAction(null);
      setArchiveReason('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-red-500" />
            <h2 className="text-xl font-semibold text-gray-900">
              Deal Management
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Deal Information */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {deal.business_name}
            </h3>
            {deal.business_alias && (
              <p className="text-sm text-gray-600 mb-2">
                Alias: {deal.business_alias}
              </p>
            )}
            {deal.description && (
              <p className="text-sm text-gray-600 mb-2">
                {deal.description}
              </p>
            )}
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>
                Value: {deal.deal_value ? `$${deal.deal_value.toLocaleString()} ${deal.currency}` : 'Not specified'}
              </span>
              <span>
                Created: {new Date(deal.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>

          {/* Action Selection */}
          {!action && (
            <div className="space-y-4">
              <p className="text-gray-700">
                What would you like to do with this deal?
              </p>
              
              {/* Archive Option */}
              <button
                onClick={() => setAction('archive')}
                className="w-full p-4 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-left"
              >
                <div className="flex items-center space-x-3">
                  <Archive className="h-5 w-5 text-blue-500" />
                  <div>
                    <h4 className="font-medium text-gray-900">Archive Deal</h4>
                    <p className="text-sm text-gray-600">
                      Preserve all data for later recovery. Deal will be hidden from normal views.
                    </p>
                  </div>
                </div>
              </button>

              {/* Delete Option */}
              <button
                onClick={() => setAction('delete')}
                className="w-full p-4 border border-red-200 rounded-lg hover:bg-red-50 transition-colors text-left"
              >
                <div className="flex items-center space-x-3">
                  <Trash2 className="h-5 w-5 text-red-500" />
                  <div>
                    <h4 className="font-medium text-gray-900">Permanently Delete</h4>
                    <p className="text-sm text-gray-600">
                      Completely remove deal and all related data. This action cannot be undone.
                    </p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Archive Form */}
          {action === 'archive' && (
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                <Archive className="h-5 w-5 text-blue-500" />
                <span className="text-blue-700 font-medium">Archive Deal</span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for archiving (optional)
                </label>
                <textarea
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  placeholder="Enter reason for archiving this deal..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                />
              </div>

              <div className="flex items-start space-x-2 p-3 bg-blue-50 rounded-lg">
                <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-700">
                  Archived deals can be restored later. All financial data, documents, and access permissions will be preserved.
                </p>
              </div>
            </div>
          )}

          {/* Delete Confirmation */}
          {action === 'delete' && (
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
                <Trash2 className="h-5 w-5 text-red-500" />
                <span className="text-red-700 font-medium">Permanently Delete Deal</span>
              </div>
              
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="font-medium text-red-900 mb-2">⚠️ Warning: This action cannot be undone</h4>
                <p className="text-sm text-red-700 mb-3">
                  This will permanently delete:
                </p>
                <ul className="text-sm text-red-700 space-y-1 ml-4">
                  <li>• Deal information and metadata</li>
                  <li>• All financial data (P&L, statements, etc.)</li>
                  <li>• All documents and extractions</li>
                  <li>• All access permissions</li>
                  <li>• All NDA documents and invitations</li>
                  <li>• All proforma and LOI documents</li>
                </ul>
              </div>

              <div className="flex items-start space-x-2 p-3 bg-yellow-50 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-yellow-700">
                  Consider archiving instead to preserve data for future reference.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          
          {action && (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`px-4 py-2 text-white rounded-lg font-medium disabled:opacity-50 ${
                action === 'delete' 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                action === 'delete' ? 'Permanently Delete' : 'Archive Deal'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
